/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  useState,
  useRef,
  useTransition,
  useEffect,
  SetStateAction,
} from 'react';
import { FaAngleUp, FaAngleDown } from 'react-icons/fa6';
import { CheckCircle, Warning } from '@phosphor-icons/react';
import { BounceLoader } from 'react-spinners';
import {
  Profile,
  PromptVersionControl,
  OpenAICredDisplay,
} from '@Models/types';
import colors from '@Common/assets/colors';

interface Props {
  profile: Profile;
  prompts: PromptVersionControl[];
  onDeployVersion: (input: OpenAICredDisplay) => any;
  onAddPrompt: (
    email: string,
    prompt: string,
    version: string,
    created_by: {
      name: string;
      email: string;
    },
    nickname?: string
  ) => any;
}

const PromptSection = ({
  profile,
  prompts,
  onDeployVersion: deployVersion,
  onAddPrompt: addPrompt,
}: Props) => {
  const [versionListActive, setVersionListActive] = useState(false);
  const [isChangesMade, setIsChangesMade] = useState(false);
  const [selectedVersion, setSelectedVersion] =
    useState<PromptVersionControl | null>(null);
  const versionListRef = useRef<HTMLDivElement>(null);
  const [nickname, setNickname] = useState<string | undefined>(undefined);
  const currentVersion = prompts.find(
    (prompt) => prompt.content === profile.prompt
  );
  const [isPending, startTransition] = useTransition();
  const [deployed, setDeployed] = useState<PromptVersionControl | undefined>(
    currentVersion
  );
  const [promptList, setPromptList] = useState<PromptVersionControl[]>(prompts);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState<'success' | 'error' | ''>('');
  const [isDeploying, setIsDeploying] = useState(false);

  const handleVersionList = () => {
    setVersionListActive(!versionListActive);
  };

  useEffect(() => {
    setPromptList(prompts);
    if (currentVersion) {
      setSelectedVersion(currentVersion);
    }
  }, [prompts, currentVersion]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        versionListRef.current &&
        !versionListRef.current.contains(event.target as Node)
      ) {
        setVersionListActive(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setVersionListActive(false);
      }
    };

    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  const [openAICreds, setopenAIRailCreds] = useState({
    openai_prompt: profile.prompt,
    openai_api_key: profile.openai_api_key,
    version: profile.version,
  });

  const handleSwitchVersion = (prompt: PromptVersionControl) => {
    setSelectedVersion(prompt);
    setopenAIRailCreds({
      ...openAICreds,
      openai_prompt: String(prompt.content),
      version: String(prompt.version),
    });
    setVersionListActive(false);
  };

  const handleCancelChanges = () => {
    setIsChangesMade(!isChangesMade);
    setopenAIRailCreds({
      ...openAICreds,
      openai_prompt: String(profile.prompt),
      version: String(profile.version),
    });
    setSelectedVersion(null);
    setNickname(undefined);
  };

  const handleDeployVersion = () => {
    const { openai_prompt, version } = openAICreds;

    if (
      !openai_prompt?.includes('{user_story}') ||
      !openai_prompt.includes('{test_collection}')
    ) {
      setMessage(
        'Prompt must include placeholders {user_story} and {test_collection}'
      );
      setMessageType('error');
      return;
    }

    const email = localStorage.getItem('email');

    if (version === deployed?.version) {
      setMessage('Already deployed');
      setMessageType('error');
      return;
    }

    const input: OpenAICredDisplay = {
      email: String(email),
      prompt: String(openai_prompt),
      version: String(version),
      openai_api_key: '',
    };

    setIsDeploying(true); // Set deploying state to true

    startTransition(() => {
      deployVersion(input)
        .then(() => {
          handleSwitchVersion(selectedVersion!);
          setDeployed(selectedVersion!);
          setMessage('OpenAI Prompt updated successfully');
          setMessageType('success');
        })
        .catch((error: { message: SetStateAction<string> }) => {
          setMessage(error.message);
          setMessageType('error');
        })
        .finally(() => {
          setIsDeploying(false); // Set deploying state to false after process is complete
        });
    });
  };

  const handleAddPrompt = () => {
    const { openai_prompt } = openAICreds;

    if (openai_prompt === '') {
      setMessage('Please input a prompt');
      setMessageType('error');
      return;
    }

    if (
      !openai_prompt?.includes('{user_story}') ||
      !openai_prompt.includes('{test_collection}')
    ) {
      setMessage(
        'Prompt must include placeholders {user_story} and {test_collection}'
      );
      setMessageType('error');
      return;
    }

    const email = String(profile.email);
    const created_by = {
      name: profile.name,
      email,
    };

    const prompt = String(openai_prompt);
    const updatedVersion = getNextVersion();

    startTransition(() => {
      addPrompt(email, prompt, updatedVersion, created_by, nickname)
        .then((data: any) => {
          setPromptList((prev) => [data, ...prev]);
          setSelectedVersion(data);
          setopenAIRailCreds({
            ...openAICreds,
            openai_prompt: data.content,
            version: data.version,
          });
          setMessage(`Version ${updatedVersion} created`);
          setMessageType('success');
        })
        .catch((error: { message: SetStateAction<string> }) => {
          setMessage(error.message);
          setMessageType('error');
        })
        .finally(() => {
          setIsChangesMade(false);
        });
    });
  };

  const getHighestVersion = (): string => {
    return promptList.sort((a, b) => {
      const versionA = a.version.split('.').map(Number);
      const versionB = b.version.split('.').map(Number);

      for (let i = 0; i < Math.max(versionA.length, versionB.length); i++) {
        if ((versionA[i] || 0) > (versionB[i] || 0)) return -1;
        if ((versionA[i] || 0) < (versionB[i] || 0)) return 1;
      }
      return 0;
    })[0].version;
  };

  const getNextVersion = () => {
    const highestVersion = getHighestVersion();
    const [major, minor] = String(highestVersion).split('.').map(Number);
    const updatedVersion = `${major}.${minor + 1}`;
    return updatedVersion;
  };

  return (
    <div className="flex h-full flex-col gap-4 sm:flex-row sm:gap-6">
      <div className="flex h-full w-full gap-4 rounded-lg border border-gray-300 bg-white p-4 px-4 sm:gap-10 sm:p-5 sm:px-8 lg:gap-36">
        <div className="flex h-full w-full flex-col items-start gap-2 sm:flex-row sm:gap-4">
          <label className="font-semibold text-gray-700">Prompt</label>

          <div className="relative flex h-full w-full flex-grow flex-col sm:w-auto">
            {!isChangesMade && (
              <div
                className={`flex cursor-pointer flex-col justify-between gap-2 sm:flex-row sm:items-center sm:justify-start sm:gap-4 sm:pb-5`}
                onClick={handleVersionList}
              >
                <div
                  className={`flex w-full flex-grow items-center gap-2 rounded-lg border px-2 py-1 sm:w-auto sm:px-4 sm:py-2 ${versionListActive ? `border-gray-400` : `border-gray-300`}`}
                >
                  <div className="flex-grow text-sm sm:text-base">
                    {selectedVersion ? (
                      <>
                        Version {selectedVersion.version}{' '}
                        {selectedVersion.nickname && (
                          <>- {selectedVersion.nickname}</>
                        )}
                      </>
                    ) : (
                      <div className="text-gray-400">Version Catalog</div>
                    )}
                  </div>
                  {versionListActive ? (
                    <FaAngleUp size={12} />
                  ) : (
                    <FaAngleDown size={12} />
                  )}
                </div>

                <button
                  className={`mb-2 mt-2 rounded-lg px-2 py-1 text-sm font-semibold text-white sm:mt-0 sm:px-4 sm:py-2 ${!selectedVersion ? `cursor-not-allowed bg-blue-300` : `bg-primary-700 hover:bg-blue-500`}`}
                  disabled={!selectedVersion || isDeploying}
                  onClick={() => handleDeployVersion()}
                >
                  {isDeploying ? (
                    <div className="flex items-center gap-2">
                      <BounceLoader
                        size={20}
                        color={colors.brandWarning[500]}
                      />
                      <span>Activating...</span>
                    </div>
                  ) : (
                    'Set as Active'
                  )}
                </button>
              </div>
            )}

            {messageType === 'error' && (
              <div className="mb-3 flex items-center justify-end pr-[105px] font-bold text-red-500">
                <Warning size={20} weight="bold" className="mr-2" />
                <span>{message}</span>
              </div>
            )}
            {messageType === 'success' && (
              <div className="mb-3 flex items-center justify-end pr-[105px] font-bold text-green-500">
                <CheckCircle size={20} weight="bold" className="mr-2" />
                <span>{message}</span>
              </div>
            )}
            {versionListActive && (
              <div className="absolute top-[30px] z-10 mt-1 flex max-h-60 w-full flex-col overflow-y-auto overflow-x-hidden rounded border border-gray-300 bg-white text-xs font-normal text-gray-600 shadow-md">
                {promptList.map((prompt) => (
                  <div
                    key={`${prompt.version} - ${prompt.date}`}
                    className={`flex cursor-pointer flex-row justify-between px-3 py-1 hover:bg-blue-100 hover:font-semibold ${
                      prompt.version === deployed?.version
                        ? 'font-medium text-green-500'
                        : ''
                    }`}
                    onClick={() => handleSwitchVersion(prompt)}
                  >
                    <div className="flex w-1/2 flex-row items-center md:w-auto">
                      <div className="">
                        Version {prompt.version}{' '}
                        {prompt.nickname && <>- {prompt.nickname}</>}
                      </div>
                      {prompt.is_personal !== undefined &&
                        !prompt.is_personal && (
                          <div className="ml-1 h-3 min-w-3 content-center justify-center rounded-full border-2 border-blue-500 bg-blue-100 px-0 font-medium text-blue-500 sm:h-4 sm:border sm:px-3 md:ml-2">
                            <span className="hidden sm:inline">Global</span>
                          </div>
                        )}
                    </div>
                    <div className="ml-2 w-3/4 border-l-2 pl-5 md:w-auto md:border-none">
                      Created by {prompt.created_by.name} on {prompt.date}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {isChangesMade && (
              <div className="mb-4 mr-2 mt-4 flex items-center justify-between gap-2 sm:gap-4">
                <div className="flex w-full items-center sm:w-auto">
                  <input
                    type="text"
                    value={nickname || ''}
                    onChange={(e) => setNickname(e.target.value)}
                    placeholder="Set nickname"
                    className="w-full rounded border border-gray-300 px-2 py-1 font-normal outline-none sm:w-auto sm:px-4 sm:py-2"
                    maxLength={150}
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    className="h-[29.6px] rounded px-2 py-1 text-sm font-medium text-gray-400 hover:border-gray-600 hover:bg-gray-200 hover:text-gray-500"
                    onClick={handleCancelChanges}
                  >
                    Cancel
                  </button>
                  <button
                    className={`h-[29.6px] rounded px-2 py-1 text-white sm:px-4 sm:py-2 ${
                      !isChangesMade
                        ? `cursor-not-allowed bg-blue-300`
                        : `bg-blue-400 hover:bg-blue-500`
                    }`}
                    disabled={!isChangesMade || isPending}
                    onClick={handleAddPrompt}
                  >
                    Create New Version
                  </button>
                </div>
              </div>
            )}

            <textarea
              data-testid="openai_prompt"
              value={openAICreds.openai_prompt}
              onChange={(e) => {
                setopenAIRailCreds({
                  ...openAICreds,
                  openai_prompt: e.target.value,
                });
                setIsChangesMade(true);
              }}
              rows={5}
              placeholder="Prompt"
              className="h-full min-w-0 flex-grow rounded-lg border border-gray-300 p-4 py-2 font-normal text-gray-600 outline-none"
              style={{ resize: 'none' }}
            ></textarea>

            <div className="flex flex-col justify-between text-xs font-normal text-gray-400 sm:flex-row sm:pt-2 sm:text-sm md:pt-3 md:text-base lg:pt-3">
              <div className="flex flex-row items-center pt-2">
                Current Version:{' '}
                {selectedVersion && !isChangesMade ? (
                  selectedVersion.version
                ) : isChangesMade ? (
                  <div className="flex flex-row items-center gap-2">
                    <div className="ml-1">{deployed?.version}</div>
                    <span>{'>>'}</span>
                    <div className="font-medium text-green-500">
                      {getNextVersion()}
                    </div>
                  </div>
                ) : (
                  deployed?.version
                )}
              </div>

              <div>
                Created by{' '}
                {selectedVersion && !isChangesMade ? (
                  `${selectedVersion.created_by.name} on ${selectedVersion.date}`
                ) : isChangesMade ? (
                  <span className="font-medium text-green-500">
                    {profile.name}
                  </span>
                ) : (
                  `${deployed?.created_by.name} on ${deployed?.date}`
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PromptSection;
